/**
 * How-to content — Finance & HR (accounting, general ledger, vendors & AP, payroll, HR,
 * audit trail), Guests & Marketing (CRM, marketing, SEO) and everyone (leave, attendance).
 * Format: see guides.exec.js.
 */

export const FINANCE_GUIDES = {
  /* ═════════════════════════ ACCOUNTING ═════════════════════════ */
  main_accountant: {
    title: 'Accounting & Reports',
    intro: 'Income, expenses, tax, approvals and assets. Pick the period at the top — every tab follows it. 📊 Excel and 📄 PDF export what you see.',
    faq: [
      { q: 'Profit looks too high', a: 'Check that all expenses are recorded for the period, including approved spoilage and complimentaries.' },
      { q: 'When is VAT due?', a: 'File and pay on KRA iTax by the 20th of the following month.' },
    ],
    sections: [
      { h: 'Choose the period and read the numbers', d: 'Revenue, costs and profit at a glance.',
        steps: [
          { t: 'At the top choose **Today**, **This Week**, **This Month**, **This Year** or **Custom Range**, then click **Apply**.' },
          'Open **Dashboard**: Total Revenue, Total Expenses, Net Profit, revenue by department (rooms, food, bar, pool, conference), ADR, RevPAR, VAT collected and outstanding balances.',
          { t: 'Open **Income Statement**: Gross Revenue (VAT inclusive) − VAT 16% − Catering Levy 2% (rooms) = Net Revenue; minus expenses = NET PROFIT / (LOSS).' },
          'Click **📊 Excel** or **📄 PDF** to export.',
        ],
      },
      { h: 'Record an expense', d: 'Every shilling spent must be recorded.',
        steps: [
          'Open **Expenses** and click **+ Record Expense**.',
          { t: 'Choose the **Category**.', tip: 'Payroll & Salaries, Utilities, Supplies, Maintenance & Repairs, Marketing, Rent & Lease, Food & Beverage Purchases, Laundry & Linen, Transport & Fuel, Taxes & Levies, Insurance or Other.' },
          'Set the **Date** and type the **Description** (what it was for) and **Amount (KES)**.',
          'Choose the **Payment Mode** and type the **Vendor / Supplier** (who was paid).',
          { t: 'Type the **Invoice Number** and **Receipt Ref / Transaction Code** (M-Pesa code, bank ref).', tip: 'Keep the paper receipt and write the expense reference on it.' },
          'Save.',
        ],
        result: 'Profit on this screen and on the GM Dashboard updates immediately.',
        mistakes: ['Recording expenses days later — reports are wrong in between.', 'Recording a spoilage or complimentary twice; those are posted automatically when approved.'],
      },
      { h: 'Find or fix an expense', d: 'Search and correct.',
        steps: ['In **Expense Filters** choose category, dates, status or search vendor/description, then **🔍 Apply Filter**.', 'Click **Edit** to correct or **Del** to delete. **✕ Clear** removes filters.'],
      },
      { h: 'File VAT and catering levy', d: 'Monthly tax returns.',
        steps: [
          'Choose last month as the period and open **VAT Report**.',
          { t: 'Read **Net Output VAT Payable** (16%).', tip: 'File and pay on the KRA iTax portal by the 20th of the following month.' },
          'Read **Catering Levy Payable (2%)** on room charges and remit it to the Tourism Fund.',
          'Export the report as evidence.',
        ],
      },
      { h: 'Approve funds for a shopping list', d: 'Release money for purchases.',
        steps: [
          'Open **Purchase Requests**. Lists here are already manager-approved.',
          'Open a list and check the items and **Estimated Total**.',
          { t: 'Click **✅ Approve Funds** — add notes if you set conditions.', tip: 'Or **✕ Reject** with a reason (required).' },
        ],
        result: 'The storekeeper can now print the list and buy.',
      },
      { h: 'Record funds given out', d: 'Cash or M-Pesa handed to staff or suppliers for purchases.',
        steps: [
          'Open **Funds Dispatched** → **Record Funds Dispatched**.',
          'Optionally choose **Link to Shopping List**. Type the amount.',
          'Choose the **Payment Method** (Cash, M-Pesa, Bank Transfer, Cheque), type **Dispatched To** and the **Reference**.',
          'Save.',
        ],
        result: 'It is recorded and posted as an expense automatically.',
      },
      { h: 'Approve spoilage reports', d: 'Kitchen waste reported by the chef.',
        steps: [
          'Open **Spoilage Review**. Read the items, values and the preventive action.',
          { t: 'Approve — the value is posted to Food & Beverage expenses and ingredient stock is reduced.' },
          'Or **❌ Reject** with a reason. Export with **Export to Excel** / **Export to PDF**.',
        ],
      },
      { h: 'Keep the assets register', d: 'Equipment, furniture and vehicles, with depreciation.',
        steps: [
          'Open **Assets Register** and click **+ Add Asset**.',
          'Type the **Asset Name** (e.g. Industrial Dishwasher — Kitchen), **Asset Type** (Fixed or Current), **Category**, **Purchase Date** and **Purchase Cost (KES)**.',
          'Add location, serial number, supplier and invoice number.',
          { t: 'Under **📉 DEPRECIATION** choose the **Method**, **Useful Life (years)** and **Salvage / Residual Value (KES)** — or an **Annual Rate (%)**.', tip: 'Net Book Value and the annual depreciation charge are calculated for you.' },
          'Add **Warranty Expiry** and **Insurance Policy No.** and save.',
        ],
      },
      { h: 'Other tabs', d: 'Occupancy, payments, rollover, reports.',
        steps: [
          '**Occupancy** — occupancy rate, room revenue, rooms and guests. **Payments** — breakdown by Cash, M-Pesa, Bank, PDQ/Card and Room Charge.',
          '**Daily Log** — the daily rollover (night audit) history; **🔄 Run Now** runs it manually.',
          '**Hotel Reports** — choose any report on the left and export it.',
          '**Audit & Logs** — export stock movements by date and category (up to 5,000).',
          '**Payroll** and **Donations** — payroll cost summary and donations received.',
        ],
      },
    ],
  },

  /* ═════════════════════════ GENERAL LEDGER ═════════════════════════ */
  general_ledger: {
    title: 'General Ledger',
    intro: 'Double-entry bookkeeping: accounts, journals and financial statements.',
    faq: [
      { q: 'The entry will not post', a: 'Total debits must equal total credits and you need at least 2 lines with accounts.' },
      { q: 'I posted a wrong entry', a: 'Use ↩ Reverse to create a reversing entry, then post the correct one. Posted entries are never deleted.' },
    ],
    sections: [
      { h: 'Set up the chart of accounts', d: 'The list of accounts everything is posted to.',
        steps: [
          'Open **🗂 Chart of Accounts** and click **+ Add Account**.',
          'Type the **Code** (e.g. 1010) and **Account Name** (e.g. Cash at Bank).',
          { t: 'Choose the **Type** (Asset, Liability, Equity, Revenue, Expense) and **Sub-type** (e.g. current_asset).' },
          'Click **Save Account**.',
        ],
      },
      { h: 'Post a journal entry', d: 'Record a transaction in the ledger.',
        steps: [
          'Open **📒 Journal Entries** and click **+ New Journal Entry**.',
          'Set the **Date** and type the narration.',
          { t: 'Add at least 2 lines with **+ Add Line**: choose the account and type the debit or credit.', tip: 'Example — paying rent from the bank: debit Rent Expense 50,000; credit Cash at Bank 50,000.' },
          { t: 'Check that total debits equal total credits, then save. It is saved as a draft.' },
          'Click **✓ Post** to put it in the ledger.',
        ],
        result: '“Entry posted to ledger”. Statements update.',
      },
      { h: 'Undo a posted entry', d: 'Correct a mistake the proper way.',
        steps: ['Find the entry and click **↩ Reverse**.', 'A reversing entry is created. Then post the correct entry.'],
      },
      { h: 'Produce statements', d: 'For management and the auditor.',
        steps: [
          '**⚖️ Trial Balance** — every account balance; total debits must equal total credits.',
          '**📊 P&L Statement** — choose **From** and **To**, click **Generate P&L**.',
          '**🏦 Balance Sheet** — assets, liabilities and equity; the balance check must agree.',
          'Click **⬇ CSV** or **🖨 Print** on any statement.',
        ],
      },
    ],
  },

  /* ═════════════════════════ VENDORS & AP ═════════════════════════ */
  vendors_ap: {
    title: 'Vendors & Accounts Payable',
    intro: 'Suppliers, the invoices they send you, what you have paid and what is overdue.',
    faq: [{ q: 'Why is an invoice in the “Over 90” column?', a: 'It is more than 90 days past its due date. Pay it or agree a plan with the supplier.' }],
    sections: [
      { h: 'Add a supplier', d: 'Everyone who bills you.',
        steps: [
          'Open **🏭 Suppliers** and click **+ Add Vendor**.',
          'Type the **Supplier / Company Name** (e.g. Nairobi Foods Ltd) and choose the **Category**.',
          { t: 'Set the **Payment Terms** (days after invoice).', tip: 'Due dates on their invoices are calculated from this.' },
          'Type the **Contact Person**, **Phone**, **Email**, **KRA PIN** and **Physical Address**.',
          'Type the **Bank Name** and **Account Number** for payments, then save.',
        ],
      },
      { h: 'Record a supplier invoice', d: 'Track what you owe.',
        steps: [
          'Open **📄 AP Invoices** and click **+ Record Invoice**.',
          'Choose the **Supplier / Vendor** and type the **Invoice Number** (e.g. INV-2025-00123).',
          'Type the **Amount (KES)**, **Invoice Date** and **Due Date**, and the **Description / Purpose**.',
          'Save.',
        ],
      },
      { h: 'Pay an invoice', d: 'Full or part payment.',
        steps: [
          'Find the invoice and click **💳 Pay**.',
          { t: 'Check the **BALANCE DUE** and type the **Amount to Pay (KES)**.', tip: 'You can pay part now and the rest later — the balance is tracked.' },
          'Set the **Payment Date**, choose the **Payment Method** (Bank Transfer, M-Pesa, Cash, Cheque) and type the **Reference Number**.',
          'Save.',
        ],
        result: '“Payment recorded successfully” and the balance drops.',
      },
      { h: 'Check what is overdue', d: 'Weekly check.',
        steps: [
          'Open **⏱ Ageing Report**.',
          'Read unpaid balances by **Current**, **1–30 Days**, **31–60 Days**, **61–90 Days** and **Over 90**.',
          'Pay the oldest first and talk to suppliers about anything over 60 days.',
        ],
      },
    ],
  },

  /* ═════════════════════════ PAYROLL ═════════════════════════ */
  payroll: {
    title: 'Payroll',
    intro: 'Kenya payroll with PAYE, NSSF, SHA (NHIF) and Housing Levy calculated for you.',
    tip: 'Confirm the statutory rates match current KRA and SHA rules before the first live run.',
    faq: [{ q: 'I cannot change this month’s payroll', a: 'It is locked. Locked payroll cannot be edited; correct it in the next month.' }],
    sections: [
      { h: 'Add an employee to payroll', d: 'Everyone who gets paid.',
        steps: [
          'Open **Employees** and click **+ Add Employee**.',
          'Personal: **Full Name**, **Employee ID** (e.g. EMP-001), department, job title, **ID Number**, **KRA PIN**, **NSSF Number**, **NHIF / SHA Number**, phone and email.',
          { t: 'Salary: **Basic Salary (KES)** plus House, Transport, Medical and Other allowances.', tip: 'Statutory deductions are calculated from these.' },
          'Choose the **Employment Type** (Permanent, Contract, Casual, Intern), **Start Date** and **Status**.',
          'Enter any **Loan Balance** or **Advance Balance**.',
          'Type the **Bank Name**, **Account Number** and **Branch**, then **✓ Save Employee**.',
        ],
      },
      { h: 'Run the monthly payroll', d: 'Calculate everyone’s pay.',
        steps: [
          'Open **Process Payroll** and choose the **Payroll Period**.',
          'Process it. Add any bonus or loan deduction for individuals.',
          { t: 'Check every line: gross, deductions and **Net Pay**.', tip: 'Compare with last month — big differences usually mean a typing error.' },
          { t: 'When everything is right, click **🔒 Lock Payroll**.', tip: 'Locking cannot be undone. Corrections then go into next month.' },
          'Click **📊 Export Bank CSV** and upload it to your bank for payment.',
        ],
        result: 'The period shows 🔒 Locked and is ready for payslips.',
      },
      { h: 'Give payslips and pay statutory dues', d: 'After payroll is locked.',
        steps: [
          'Open **Payslips** → **Generate Payslips**, then **🖨️ Print Payslip** for each employee.',
          'Open **Statutory** for totals to pay KRA (PAYE and Housing Levy), NSSF and SHA.',
          'Open **Reports** for the year-to-date payroll summary.',
        ],
      },
    ],
  },

  /* ═════════════════════════ HR ═════════════════════════ */
  main_hr: {
    title: 'Human Resources',
    intro: 'Leave approvals and staff access.',
    sections: [
      { h: 'Decide on a leave request', d: 'Approve or reject staff leave.',
        steps: [
          'Open **Leave Requests** and open a pending request.',
          'Check the dates against the staff roster.',
          { t: 'Type **HR Notes** — optional for approval, required to reject.' },
          'Approve or Reject.',
        ],
        result: 'The employee is notified. On approved leave their account is locked until you reactivate it.',
      },
      { h: 'Terminate or reactivate staff', d: 'Control who can log in.',
        steps: [
          'Open **Staff Accounts** and search by name, username or role.',
          'Click **Terminate** for someone who has left — their account is deactivated.',
          'Click **Reactivate** for someone returning from leave or suspension.',
        ],
      },
      { h: 'Payroll', d: 'Where HR runs payroll.',
        steps: ['Open **Payroll** in the sidebar to manage employees, process and lock payroll, and print payslips.'] },
    ],
  },

  /* ═════════════════════════ AUDIT ═════════════════════════ */
  audit: {
    title: 'Audit Trail',
    intro: 'Every important action, who did it and when — for investigations and control.',
    sections: [
      { h: 'Investigate an action', d: 'Find out who did what.',
        steps: [
          { t: 'In **Filter Audit Log** choose the **Action Type**.', tip: 'Login, Order Voided, Bill Paid, Bill Reopened, Payment Requested, Discount Applied, Refund Issued, Price Changed, Check-In/Out, Folio Charge, User Created, Stock Adjusted and more.' },
          'Narrow by **Role**, **User**, keyword (**Search Description**) and dates.',
          'Click any row to see full details and additional data.',
          'Click **📥 Export Excel** to keep a copy.',
        ],
      },
      { h: 'Spot risks', d: 'Patterns worth checking.',
        steps: ['Open **Insights**: action breakdown, most active users, activity by role and security & critical events.', 'Look for many voids, refunds or discounts by one person.'] },
    ],
  },

  /* ═════════════════════════ CRM ═════════════════════════ */
  crm: {
    title: 'Guest CRM',
    intro: 'Know your guests: profiles, loyalty, complaints and satisfaction.',
    sections: [
      { h: 'Create or update a guest profile', d: 'Remember guests’ preferences.',
        steps: [
          { t: 'Profiles are created automatically at checkout with loyalty points. For others, click **+ New Profile** (name and ID required).' },
          { t: 'Fill **Preferences / Special Requests** (e.g. ground floor, vegetarian, anniversary in June) and notes.', tip: 'Reception sees this on the next visit — it turns guests into regulars.' },
          'Click **💾 Save Profile**. Use **Edit** later.',
          'Search by name, ID, email or phone; filter by loyalty tier.',
        ],
      },
      { h: 'Log and resolve a complaint', d: 'Make sure every complaint is followed up.',
        steps: [
          'Open **⚠️ Complaints** → **+ Log Complaint**.',
          'Choose the **Guest Profile**, **Category**, **Date Reported** and describe the issue in detail.',
          'Click **Submit Complaint**.',
          'When it is handled, click **✓ Resolve**.',
        ],
        result: 'Open complaints show on the GM Dashboard until resolved.',
      },
      { h: 'Record guest satisfaction (NPS)', d: 'How likely guests are to recommend you.',
        steps: [
          'Open **⭐ NPS** → **+ Add NPS Score**.',
          'Choose the guest, type the **Score (0–10)** and their comment, then **Submit Score**.',
          'Open **📊 Summary** for the Net Promoter Score and loyalty tier distribution.',
        ],
      },
    ],
  },

  /* ═════════════════════════ MARKETING ═════════════════════════ */
  main_marketing: {
    title: 'Marketing & Promotions',
    intro: 'Plan campaigns, create offers, message guests by SMS and email, and generate content.',
    faq: [{ q: 'SMS or email did not send', a: 'Africa’s Talking (SMS) and Brevo (email) must be set up on the server by the administrator.' }],
    sections: [
      { h: 'Plan a campaign', d: 'Organise and track marketing activity.',
        steps: [
          'Open **Campaigns** → **+ New Campaign**.',
          'Type the **Campaign Title** and choose the **Campaign Type** (social media, email, SMS, print, influencer, promotions, events, partnerships).',
          'Type the **Channel / Platform**, **Target Audience**, **Budget (KES)**, start and end dates and who it is **Assigned To**.',
          { t: 'Set the **Goal / Objective** and **KPI / Success Metric** (e.g. 50 bookings, CTR).', tip: 'A clear goal lets you judge later whether it worked.' },
          'Write the brief, choose the status and **✓ Save Campaign**. Use **Activate**, **Pause**, **Edit** or **Del** later.',
        ],
      },
      { h: 'Create a promotion', d: 'Offers with a promo code.',
        steps: [
          'Open **Promotions** → **+ New Promotion**.',
          'Type the **Promotion Title** (e.g. 20% Off Weekend Stays) and choose the **Promo Type**.',
          'Type the **Promo Code** (e.g. SAVE20) and **Discount %**.',
          'Set **Valid From** / **Valid Until** and the **Terms & Conditions**, then **✓ Create Promotion**.',
        ],
      },
      { h: 'Send a bulk SMS', d: 'Offers straight to guests’ phones.',
        steps: [
          'Open **Bulk SMS**. Pick a **QUICK TEMPLATE** or write your own **Message**.',
          { t: 'Keep it under 480 characters (3 SMS).', tip: 'About KES 1.20 per SMS. Short messages cost less.' },
          'Choose the **TARGET AUDIENCE**: All Guests, Currently Checked-In, Last 30/90 Days, VIP / Repeat Guests — or type phone numbers (+254712345678, one per line).',
          'Optionally schedule it. Click **👁 Preview Campaign**, check, then send.',
        ],
      },
      { h: 'Send a bulk email', d: 'Newsletters and offers by email.',
        steps: [
          'Open **Bulk Email**. Pick a template (Weekend Getaway, Conference Package, Loyalty Offer) or write your own.',
          'Type the **From Name**, **Subject Line** and a short **Preview Text**.',
          'Write the **Email Body** and click **👁 Preview Email**, then send.',
        ],
      },
      { h: 'Generate content with AI', d: 'Posts, SMS, blog outlines and replies in seconds.',
        steps: [
          'Open **AI Content Studio** and choose the content type (Social Media Post, SMS Campaign, Blog Post Outline, SEO Meta Tags, Promotion Copy, Review Response, SEO Page Audit).',
          { t: 'Describe what you want, or click **Try example prompt**, then press Ctrl+Enter.' },
          'Refine with **🔄 Generate New**, **✂️ Make Shorter** or **🔥 More Compelling**.',
        ],
      },
      { h: 'Reviews, calendar and analytics', d: 'Keep track.',
        steps: ['**Dashboard** — active campaigns, budget, ratings, promos.', '**Guest Reviews** — ratings by category.', '**Content Calendar** — plan posts and newsletters.', '**Analytics** — log campaign results.'] },
    ],
  },

  /* ═════════════════════════ SEO ═════════════════════════ */
  seo: {
    title: 'SEO Intelligence',
    intro: 'Get found on Google. Uses live web search, so results reflect the market today.',
    faq: [{ q: 'Nothing happens when I search', a: 'SEO tools need an AI API key on the server. Ask the administrator.' }],
    sections: [
      { h: 'Find keywords guests search for', d: 'Words to use on your website and posts.',
        steps: [
          'Open **🔍 Keyword Research**. Optionally type a **Focus Area** (e.g. conference facilities, honeymoon).',
          'Run it and read **Primary**, **Long-Tail**, **Local SEO** and **Seasonal & Event Keywords**, and the top opportunity.',
        ],
      },
      { h: 'Write page titles and descriptions', d: 'What Google shows for your pages.',
        steps: [
          'Open **🏷️ Meta Tags** and choose the **Page**.',
          'Copy the **Title Tag**, **Meta Description**, keywords and suggested H1.',
          { t: 'Give the **JSON-LD Schema** to your web developer to paste into the page head.' },
        ],
      },
      { h: 'Learn from competitors and plan content', d: 'Beat nearby hotels online.',
        steps: [
          '**🏆 Competitor Analysis** — their keywords, gaps you can fill and ⚡ Quick Wins to do first.',
          '**✍️ Content Strategy** — blog ideas, a content calendar, social posts and local content opportunities.',
        ],
      },
    ],
  },

  /* ═════════════════════════ EVERYONE ═════════════════════════ */
  leave: {
    title: 'My Leave',
    intro: 'Apply for leave and follow your request.',
    sections: [
      { h: 'Apply for leave', d: 'Request time off.',
        steps: [
          'Click **+ New Leave Request**.',
          'Choose the **Leave Type**: Annual Leave, Sick Day, Emergency Leave, Unpaid Leave, Maternity / Paternity or Other.',
          { t: 'Set the **From Date** and **To Date**.', tip: 'The end date must be after the start date.' },
          'Type the **Reason / Details** and submit.',
        ],
        result: '“Leave request submitted — HR will review it shortly.” You are notified of the decision.',
      },
      { h: 'Track or cancel a request', d: 'Follow your leave.',
        steps: ['Your requests show here with status (Pending, Approved, Rejected) and any HR note.', 'Cancel a pending request if your plans change.'] },
      { h: 'See who is on leave (managers)', d: 'For admin and general manager.',
        steps: ['**Staff Leave Overview** shows who is on leave now, upcoming approved leave and pending requests.', 'Search by name or leave type.'] },
    ],
  },
  attendance: {
    title: 'Attendance',
    intro: 'Clock in and out so your hours are right for payroll.',
    sections: [
      { h: 'Clock in and out', d: 'Every shift.',
        steps: [
          'At the start of your shift click **Clock In**. A message confirms “Clocked in successfully!”.',
          'When you leave click **Clock Out**.',
          { t: 'Forgot? Ask the admin to add or correct the record.', tip: 'Admin → Attendance → Records → + Add.' },
        ],
      },
    ],
  },
};
